const numberFormat = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(value);
const currencyFormat = (value: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
    value
  );

// Helper function để tính responsive bar width
const getResponsiveBarWidth = (dataLength: number) => {
  if (dataLength <= 6) return '60%';
  if (dataLength <= 12) return '40%';
  return '30%';
};

// Helper function để tính responsive grid
const getResponsiveGrid = (dataLength: number) => {
  if (dataLength <= 6) {
    return { left: 60, right: 70, top: 30, bottom: 60 };
  }
  return { left: 80, right: 90, top: 30, bottom: 60 };
};

// ECharts option generator (Grouped Bar chart for monthly data, dual y-axis)
export const getGroupedBarOption = (
  monthlyData: import('@/api/gifts-requests').MonthlyData[]
) => {
  // Tìm max cho số quà, số yêu cầu, giá trị
  const maxGift = Math.max(
    ...monthlyData.map((item) => item.totalGiftsGiven),
    0
  );
  const maxReq = Math.max(...monthlyData.map((item) => item.totalRequests), 0);
  const maxValue = Math.max(...monthlyData.map((item) => item.totalValue), 0);
  // Max trục trái (số quà, số yêu cầu)
  const maxY = Math.max(maxGift, maxReq);
  const yAxis0Max = maxY <= 5 ? 5 : Math.ceil((maxY + 1) / 5) * 5;
  // Tính tỷ lệ giữa maxValue và yAxis0Max để có cùng số đường kẻ ngang
  const ratio = maxValue / yAxis0Max;
  // Làm tròn tỷ lệ lên mức 10^n gần nhất
  const logRatio = Math.log10(ratio);
  const roundedLog = Math.ceil(logRatio);
  const multiplier = Math.pow(10, roundedLog);
  // Tính max trục phải dựa trên tỷ lệ
  const finalMax = yAxis0Max * multiplier;

  const barWidth = getResponsiveBarWidth(monthlyData.length);
  const grid = getResponsiveGrid(monthlyData.length);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: Array<{ axisValue: string; value: number }>) => {
        const [gift, value, req] = params;
        const percentGift = maxGift
          ? Math.round((gift.value / yAxis0Max) * 100)
          : 0;
        const percentValue = maxValue
          ? Math.round((value.value / finalMax) * 100)
          : 0;
        const percentReq = maxReq
          ? Math.round((req.value / yAxis0Max) * 100)
          : 0;
        return (
          `Tháng ${gift.axisValue}<br />` +
          `<span style='color:#355E3B'>●</span> Số quà: <b>${numberFormat(
            gift.value
          )}</b> (${percentGift}%)<br />` +
          `<span style='color:#2563eb'>●</span> Giá trị: <b>${currencyFormat(
            value.value
          )}</b> (${percentValue}%)<br />` +
          `<span style='color:#f59e42'>●</span> Số yêu cầu: <b>${numberFormat(
            req.value
          )}</b> (${percentReq}%)`
        );
      },
    },
    legend: {
      data: ['Số quà', 'Giá trị', 'Số yêu cầu'],
      bottom: 10,
      icon: 'circle',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 14 },
    },
    grid,
    xAxis: {
      type: 'category',
      data: monthlyData.map((item) => `Th${item.month}`),
      axisLabel: {
        fontSize: monthlyData.length > 6 ? 11 : 13,
        interval: 0,
        rotate: monthlyData.length > 6 ? 45 : 0,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Số quà / Số yêu cầu',
        axisLabel: {
          formatter: (value: number) => numberFormat(value),
          fontSize: 13,
        },
        interval: 1,
        minInterval: 1,
        min: 0,
        max: yAxis0Max,
      },
      {
        type: 'value',
        name: 'Giá trị',
        axisLabel: {
          formatter: (value: number) => currencyFormat(value),
          fontSize: 13,
        },
        position: 'right',
        offset: 0,
        min: 0,
        max: finalMax,
      },
    ],
    series: [
      {
        name: 'Số quà',
        type: 'bar',
        data: monthlyData.map((item) => item.totalGiftsGiven),
        itemStyle: { color: '#355E3B', borderRadius: [4, 4, 0, 0] },
        barGap: 0,
        barWidth,
        yAxisIndex: 0,
      },
      {
        name: 'Giá trị',
        type: 'bar',
        data: monthlyData.map((item) => item.totalValue),
        itemStyle: { color: '#2563eb', borderRadius: [4, 4, 0, 0] },
        barWidth,
        yAxisIndex: 1,
      },
      {
        name: 'Số yêu cầu',
        type: 'bar',
        data: monthlyData.map((item) => item.totalRequests),
        itemStyle: { color: '#f59e42', borderRadius: [4, 4, 0, 0] },
        barWidth,
        yAxisIndex: 0,
      },
    ],
    animation: false,
  };
};

// Order Statistics Chart Option
export const getOrderStatisticsOption = (
  monthlyData: Array<{
    month: number;
    monthName: string;
    totalOrders: number;
    totalAmount: number;
    averageAmount: number;
  }>
) => {
  // Tìm max cho số đơn hàng, doanh thu, trung bình
  const maxOrders = Math.max(...monthlyData.map((item) => item.totalOrders), 0);
  const maxAmount = Math.max(...monthlyData.map((item) => item.totalAmount), 0);

  // Max trục trái (số đơn hàng)
  const yAxis0Max = maxOrders <= 5 ? 5 : Math.ceil((maxOrders + 1) / 5) * 5;

  // Tính tỷ lệ giữa maxAmount và yAxis0Max để có cùng số đường kẻ ngang
  const ratio = maxAmount / yAxis0Max;
  const logRatio = Math.log10(ratio);
  const roundedLog = Math.ceil(logRatio);
  const multiplier = Math.pow(10, roundedLog);
  const finalMax = yAxis0Max * multiplier;

  const grid = getResponsiveGrid(monthlyData.length);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (
        params: Array<{ axisValue: string; value: number; seriesName: string }>
      ) => {
        let result = `Tháng ${params[0].axisValue}<br />`;
        params.forEach((param) => {
          if (param.seriesName === 'Số đơn hàng') {
            const percent = maxOrders
              ? Math.round((param.value / yAxis0Max) * 100)
              : 0;
            result += `<span style='color:#f97316'>●</span> ${
              param.seriesName
            }: <b>${numberFormat(param.value)}</b> (${percent}%)<br />`;
          } else {
            const percent = maxAmount
              ? Math.round((param.value / finalMax) * 100)
              : 0;
            result += `<span style='color:#dc2626'>●</span> ${
              param.seriesName
            }: <b>${currencyFormat(param.value)}</b> (${percent}%)<br />`;
          }
        });
        return result;
      },
    },
    legend: {
      data: ['Số đơn hàng', 'Tổng doanh thu', 'Trung bình/đơn'],
      bottom: 10,
      icon: 'circle',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 14 },
    },
    grid,
    xAxis: {
      type: 'category',
      data: monthlyData.map((item) => item.monthName),
      axisLabel: {
        fontSize: monthlyData.length > 6 ? 11 : 13,
        rotate: monthlyData.length > 6 ? 45 : 0,
        interval: 0,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Số đơn hàng',
        axisLabel: {
          formatter: (value: number) => numberFormat(value),
          fontSize: 13,
        },
        interval: 1,
        minInterval: 1,
        min: 0,
        max: yAxis0Max,
      },
      {
        type: 'value',
        name: 'Doanh thu (VNĐ)',
        axisLabel: {
          formatter: (value: number) => currencyFormat(value),
          fontSize: 13,
        },
        position: 'right',
        offset: 0,
        min: 0,
        max: finalMax,
      },
    ],
    series: [
      {
        name: 'Số đơn hàng',
        type: 'bar',
        data: monthlyData.map((item) => item.totalOrders),
        itemStyle: { color: '#f97316', borderRadius: [4, 4, 0, 0] },
        barGap: 0,
        barWidth: 18,
        yAxisIndex: 0,
      },
      {
        name: 'Tổng doanh thu',
        type: 'line',
        data: monthlyData.map((item) => item.totalAmount),
        itemStyle: { color: '#dc2626' },
        yAxisIndex: 1,
        smooth: true,
      },
      {
        name: 'Trung bình/đơn',
        type: 'line',
        data: monthlyData.map((item) => item.averageAmount),
        itemStyle: { color: '#ca8a04' },
        yAxisIndex: 1,
        smooth: true,
      },
    ],
    animation: false,
  };
};

// Order Statistics Grouped Bar Chart Option (2 columns)
export const getOrderGroupedBarOption = (
  monthlyData: Array<{
    month: number;
    monthName: string;
    totalOrders: number;
    totalAmount: number;
    averageAmount: number;
  }>
) => {
  // Tìm max cho số đơn hàng và doanh thu
  const maxOrders = Math.max(...monthlyData.map((item) => item.totalOrders), 0);
  const maxAmount = Math.max(...monthlyData.map((item) => item.totalAmount), 0);

  // Max trục trái (số đơn hàng)
  const yAxis0Max = maxOrders <= 5 ? 5 : Math.ceil((maxOrders + 1) / 5) * 5;

  // Tính tỷ lệ giữa maxAmount và yAxis0Max để có cùng số đường kẻ ngang
  const ratio = maxAmount / yAxis0Max;
  const logRatio = Math.log10(ratio);
  const roundedLog = Math.ceil(logRatio);
  const multiplier = Math.pow(10, roundedLog);
  const finalMax = yAxis0Max * multiplier;

  const barWidth = getResponsiveBarWidth(monthlyData.length);
  const grid = getResponsiveGrid(monthlyData.length);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: Array<{ axisValue: string; value: number }>) => {
        const [orders, revenue] = params;
        const percentOrders = maxOrders
          ? Math.round((orders.value / yAxis0Max) * 100)
          : 0;
        const percentRevenue = maxAmount
          ? Math.round((revenue.value / finalMax) * 100)
          : 0;
        return (
          `Tháng ${orders.axisValue}<br />` +
          `<span style='color:#f97316'>●</span> Số đơn hàng: <b>${numberFormat(
            orders.value
          )}</b> (${percentOrders}%)<br />` +
          `<span style='color:#dc2626'>●</span> Doanh thu: <b>${currencyFormat(
            revenue.value
          )}</b> (${percentRevenue}%)`
        );
      },
    },
    legend: {
      data: ['Số đơn hàng', 'Doanh thu'],
      bottom: 10,
      icon: 'circle',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 14 },
    },
    grid,
    xAxis: {
      type: 'category',
      data: monthlyData.map((item) => `Th${item.month}`),
      axisLabel: {
        fontSize: monthlyData.length > 6 ? 11 : 13,
        interval: 0,
        rotate: monthlyData.length > 6 ? 45 : 0,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Số đơn hàng',
        axisLabel: {
          formatter: (value: number) => numberFormat(value),
          fontSize: 13,
        },
        interval: 1,
        minInterval: 1,
        min: 0,
        max: yAxis0Max,
      },
      {
        type: 'value',
        name: 'Doanh thu',
        axisLabel: {
          formatter: (value: number) => currencyFormat(value),
          fontSize: 13,
        },
        position: 'right',
        offset: 0,
        min: 0,
        max: finalMax,
      },
    ],
    series: [
      {
        name: 'Số đơn hàng',
        type: 'bar',
        data: monthlyData.map((item) => item.totalOrders),
        itemStyle: { color: '#f97316', borderRadius: [4, 4, 0, 0] },
        barGap: 0,
        barWidth,
        yAxisIndex: 0,
      },
      {
        name: 'Doanh thu',
        type: 'bar',
        data: monthlyData.map((item) => item.totalAmount),
        itemStyle: { color: '#dc2626', borderRadius: [4, 4, 0, 0] },
        barWidth,
        yAxisIndex: 1,
      },
    ],
    animation: false,
  };
};

// Customer Order Statistics Grouped Bar Chart Option (2 columns)
export const getCustomerOrderGroupedBarOption = (
  monthlyData: Array<{
    month: number;
    monthName: string;
    totalOrders: number;
    totalAmount: number;
    averageAmount: number;
  }>
) => {
  // Tìm max cho số đơn hàng và doanh thu
  const maxOrders = Math.max(...monthlyData.map((item) => item.totalOrders), 0);
  const maxAmount = Math.max(...monthlyData.map((item) => item.totalAmount), 0);

  // Max trục trái (số đơn hàng)
  const yAxis0Max = maxOrders <= 5 ? 5 : Math.ceil((maxOrders + 1) / 5) * 5;

  // Tính tỷ lệ giữa maxAmount và yAxis0Max để có cùng số đường kẻ ngang
  const ratio = maxAmount / yAxis0Max;
  const logRatio = Math.log10(ratio);
  const roundedLog = Math.ceil(logRatio);
  const multiplier = Math.pow(10, roundedLog);
  const finalMax = yAxis0Max * multiplier;

  const barWidth = getResponsiveBarWidth(monthlyData.length);
  const grid = getResponsiveGrid(monthlyData.length);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: Array<{ axisValue: string; value: number }>) => {
        const [orders, revenue] = params;
        const percentOrders = maxOrders
          ? Math.round((orders.value / yAxis0Max) * 100)
          : 0;
        const percentRevenue = maxAmount
          ? Math.round((revenue.value / finalMax) * 100)
          : 0;
        return (
          `Tháng ${orders.axisValue}<br />` +
          `<span style='color:#8b5cf6'>●</span> Số đơn hàng: <b>${numberFormat(
            orders.value
          )}</b> (${percentOrders}%)<br />` +
          `<span style='color:#06b6d4'>●</span> Doanh thu: <b>${currencyFormat(
            revenue.value
          )}</b> (${percentRevenue}%)`
        );
      },
    },
    legend: {
      data: ['Số đơn hàng', 'Doanh thu'],
      bottom: 10,
      icon: 'circle',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 14 },
    },
    grid,
    xAxis: {
      type: 'category',
      data: monthlyData.map((item) => `Th${item.month}`),
      axisLabel: {
        fontSize: monthlyData.length > 6 ? 11 : 13,
        interval: 0,
        rotate: monthlyData.length > 6 ? 45 : 0,
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Số đơn hàng',
        axisLabel: {
          formatter: (value: number) => numberFormat(value),
          fontSize: 13,
        },
        interval: 1,
        minInterval: 1,
        min: 0,
        max: yAxis0Max,
      },
      {
        type: 'value',
        name: 'Doanh thu',
        axisLabel: {
          formatter: (value: number) => currencyFormat(value),
          fontSize: 13,
        },
        position: 'right',
        offset: 0,
        min: 0,
        max: finalMax,
      },
    ],
    series: [
      {
        name: 'Số đơn hàng',
        type: 'bar',
        data: monthlyData.map((item) => item.totalOrders),
        itemStyle: { color: '#8b5cf6', borderRadius: [4, 4, 0, 0] },
        barGap: 0,
        barWidth,
        yAxisIndex: 0,
      },
      {
        name: 'Doanh thu',
        type: 'bar',
        data: monthlyData.map((item) => item.totalAmount),
        itemStyle: { color: '#06b6d4', borderRadius: [4, 4, 0, 0] },
        barWidth,
        yAxisIndex: 1,
      },
    ],
    animation: false,
  };
};

// Total Order Pie Chart Option
export const getTotalOrderPieOption = (
  monthlyData: Array<{
    month: number;
    monthName: string;
    totalOrders: number;
    totalAmount: number;
    averageAmount: number;
  }>
) => {
  // Tạo dữ liệu cho biểu đồ tròn (luôn hiển thị 12 tháng)
  const monthNames = [
    'Th 1',
    'Th 2',
    'Th 3',
    'Th 4',
    'Th 5',
    'Th 6',
    'Th 7',
    'Th 8',
    'Th 9',
    'Th 10',
    'Th 11',
    'Th 12',
  ];

  // Tính tổng số đơn hàng
  const totalOrders = monthlyData.reduce(
    (sum, item) => sum + item.totalOrders,
    0
  );

  const pieData = monthNames.map((monthName, index) => {
    const monthData = monthlyData.find((item) => item.month === index + 1);
    const value = monthData?.totalOrders || 0;
    return {
      name: monthName,
      value: value,
      totalOrders: totalOrders, // Thêm tổng để tính phần trăm
      itemStyle: {
        color:
          value > 0
            ? [
                '#8B5CF6',
                '#06B6D4',
                '#10B981',
                '#F59E0B',
                '#EF4444',
                '#EC4899',
                '#8B5CF6',
                '#06B6D4',
                '#8B5CF6',
                '#06B6D4',
                '#10B981',
                '#F59E0B',
              ][index % 12]
            : '#f3f4f6',
      },
    };
  });

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params: {
        name: string;
        value: number;
        data: { totalOrders: number };
      }) => {
        if (params.value === 0) {
          return `${params.name}<br/>Không có đơn hàng`;
        }
        const percentage =
          params.data.totalOrders > 0
            ? ((params.value / params.data.totalOrders) * 100).toFixed(1)
            : '0';
        return `${params.name}<br/>${numberFormat(
          params.value
        )} đơn hàng<br/>${percentage}%`;
      },
    },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      type: 'scroll',
      textStyle: { fontSize: 12 },
      formatter: (name: string) => {
        const data = pieData.find((item) => item.name === name);
        if (data && data.value > 0) {
          return `${name}: ${numberFormat(data.value)} đơn`;
        }
        return `${name}: ${numberFormat(data?.value || 0)} đơn`;
      },
    },
    series: [
      {
        name: 'Đơn hàng theo tháng',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['40%', '50%'],
        data: pieData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        label: {
          show: false,
        },
        labelLine: {
          show: false,
        },
      },
    ],
  };
};

export { numberFormat, currencyFormat };
